import type {FC} from 'react'
import {useCallback} from 'react'
import type {IncludeExcludeParameters, RulesetTarget} from '@github-ui/repos-rules-types/rules-types'
import {INCLUDE_ALL_PATTERN} from '@github-ui/repos-rules-types/rules-types'
import {announce} from '@github-ui/aria-live'
import {IncludeExcludeTarget} from './IncludeExcludeTarget'
import {useRuleStrings} from '../../../hooks/use-rule-strings'

export type WorkflowTargetProps = {
  rulesetId?: number
  readOnly: boolean
  fnmatchHelpUrl?: string
  rulesetTarget: RulesetTarget
  parameters?: IncludeExcludeParameters
  updateParameters: (parameters: IncludeExcludeParameters | undefined) => void
}

const ALL_WORKFLOWS_PARAMETERS: IncludeExcludeParameters = {include: [INCLUDE_ALL_PATTERN], exclude: []}
const isEmpty = ({include, exclude}: IncludeExcludeParameters) => include.length === 0 && exclude.length === 0

export const WorkflowTarget: FC<WorkflowTargetProps> = ({
  rulesetId,
  readOnly,
  fnmatchHelpUrl,
  rulesetTarget,
  parameters,
  updateParameters,
}) => {
  const {rulesetOrPolicy} = useRuleStrings()
  const isAllWorkflows = parameters === undefined

  const onUpdateParameters = useCallback(
    (updatedParameters: IncludeExcludeParameters) => {
      // Only remove the projected entry, not a literal pattern entered by the user.
      const scopedParameters =
        isAllWorkflows && updatedParameters.include[0] === INCLUDE_ALL_PATTERN
          ? {...updatedParameters, include: updatedParameters.include.slice(1)}
          : updatedParameters
      const nextParameters = isEmpty(scopedParameters) ? undefined : scopedParameters

      updateParameters(nextParameters)

      if (!isAllWorkflows && nextParameters === undefined) {
        announce('All workflows are targeted')
      }
    },
    [isAllWorkflows, updateParameters],
  )

  return (
    <IncludeExcludeTarget
      rulesetId={rulesetId}
      readOnly={readOnly}
      rulesetTarget={rulesetTarget}
      fnmatchHelpUrl={fnmatchHelpUrl}
      parameters={parameters ?? ALL_WORKFLOWS_PARAMETERS}
      implicitAllLabel={isAllWorkflows ? 'All workflows' : undefined}
      panelTitle="Workflow targeting criteria"
      targetType="workflow_path"
      updateParameters={onUpdateParameters}
      blankslate={{
        heading: 'No workflow targets have been added yet',
        description: !readOnly ? (
          <>
            Add an include or exclude pattern to target workflow files with this {rulesetOrPolicy}. Patterns match the
            full path, for example <code>.github/workflows/deploy.yml</code>.
          </>
        ) : undefined,
      }}
    />
  )
}
